import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  previewCoDirectorInvite,
  claimCoDirectorAccount,
  acceptCoDirectorInvite,
} from '@/lib/api/co-directors';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { isPasswordValid, PASSWORD_MIN_LENGTH } from '@/lib/passwordPolicy';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/supabaseClient';

// Story 094 — landing page for /co-director-invite/:token (public
// offering). Validates the token, then either accepts directly (already
// signed in as the invited email) or lets the invitee sign in / create a
// password and accept. On success the new co-director lands in the
// tournament hub.

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'needsPassword';
      tournamentName: string;
      inviterName: string | null;
      email: string;
      hasAccount: boolean;
    }
  | { kind: 'ready'; tournamentName: string }
  | { kind: 'wrongUser'; email: string };

export function CoDirectorInviteAcceptPage() {
  const { t } = useTranslation('director');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token || authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const preview = await previewCoDirectorInvite(token);
        if (cancelled) return;

        let sessionEmail = user?.email ?? null;
        if (!sessionEmail) {
          const { data } = await supabase.auth.getSession();
          sessionEmail = data.session?.user?.email ?? null;
        }

        if (sessionEmail) {
          if (sessionEmail.toLowerCase() === preview.email.toLowerCase()) {
            setPhase({ kind: 'ready', tournamentName: preview.tournamentName });
          } else {
            setPhase({ kind: 'wrongUser', email: preview.email });
          }
          return;
        }

        setPhase({
          kind: 'needsPassword',
          tournamentName: preview.tournamentName,
          inviterName: preview.inviterName,
          email: preview.email,
          hasAccount: preview.hasAccount,
        });
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'error',
          message:
            err instanceof ApiError
              ? err.userMessage
              : t('co_director_invite_accept.validate_failed'),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, authLoading]);

  async function finishAccept(tournamentName: string) {
    if (!token) return;
    const { tournamentId } = await acceptCoDirectorInvite(token);
    showSuccessToast(
      t('co_director_invite_accept.success', { tournamentName }),
    );
    navigate(`/tournaments/${tournamentId}`, { replace: true });
  }

  async function handleAcceptSignedIn() {
    if (phase.kind !== 'ready') return;
    setSubmitting(true);
    try {
      await finishAccept(phase.tournamentName);
    } catch (err) {
      showErrorToast(
        err instanceof ApiError
          ? err.userMessage
          : t('co_director_invite_accept.accept_failed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (phase.kind !== 'needsPassword' || !token) return;
    if (!phase.hasAccount && !isPasswordValid(password)) {
      showErrorToast(t('co_director_invite_accept.weak_password'));
      return;
    }
    setSubmitting(true);
    try {
      if (!phase.hasAccount) {
        await claimCoDirectorAccount(token, password);
      }
      const { error } = await supabase.auth.signInWithPassword({
        email: phase.email,
        password,
      });
      if (error) {
        showErrorToast(t('co_director_invite_accept.signin_failed'));
        return;
      }
      await finishAccept(phase.tournamentName);
    } catch (err) {
      showErrorToast(
        err instanceof ApiError
          ? err.userMessage
          : t('co_director_invite_accept.accept_failed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="co-director-invite-accept-page"
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
            <Crown className="h-6 w-6 text-accent-foreground" />
          </div>
          <CardTitle className="text-2xl">
            {t('co_director_invite_accept.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {phase.kind === 'loading' && (
            <p className="text-center text-muted-foreground">
              {t('co_director_invite_accept.loading')}
            </p>
          )}

          {phase.kind === 'error' && (
            <p
              data-testid="co-director-invite-error"
              className="text-center text-sm text-destructive"
            >
              {phase.message}
            </p>
          )}

          {phase.kind === 'wrongUser' && (
            <p
              data-testid="co-director-invite-wrong-user"
              className="text-center text-sm text-destructive"
            >
              <Trans
                i18nKey="co_director_invite_accept.wrong_user"
                t={t}
                values={{ email: phase.email }}
                components={{ email: <span className="font-mono" /> }}
              />
            </p>
          )}

          {phase.kind === 'ready' && (
            <>
              <p className="text-center text-sm text-muted-foreground">
                <Trans
                  i18nKey="co_director_invite_accept.intro"
                  t={t}
                  values={{ tournamentName: phase.tournamentName }}
                  components={{ b: <span className="font-semibold" /> }}
                />
              </p>
              <Button
                type="button"
                data-testid="co-director-accept-button"
                disabled={submitting}
                onClick={handleAcceptSignedIn}
              >
                {t('co_director_invite_accept.accept_button')}
              </Button>
            </>
          )}

          {phase.kind === 'needsPassword' && (
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <p className="text-center text-sm text-muted-foreground">
                <Trans
                  i18nKey="co_director_invite_accept.intro"
                  t={t}
                  values={{ tournamentName: phase.tournamentName }}
                  components={{ b: <span className="font-semibold" /> }}
                />
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="co-director-accept-password">
                  {phase.hasAccount
                    ? t('co_director_invite_accept.password_label_existing')
                    : t('co_director_invite_accept.password_label_new')}
                </label>
                <Input
                  id="co-director-accept-password"
                  type="password"
                  required
                  minLength={phase.hasAccount ? undefined : PASSWORD_MIN_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="co-director-accept-password"
                  autoComplete={phase.hasAccount ? 'current-password' : 'new-password'}
                />
              </div>
              <Button
                type="submit"
                data-testid="co-director-accept-submit"
                disabled={submitting}
              >
                {submitting
                  ? t('co_director_invite_accept.submitting')
                  : t('co_director_invite_accept.accept_button')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
